import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Boxes, Loader2, Search, Wand2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { BotonCostoCorrecto, CostoRevisadoLista } from "@/components/CostoRevisado";

const formatCOP = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const CONF = {
  alta: { label: "Alta", cls: "bg-emerald-100 text-emerald-800" },
  media: { label: "Media", cls: "bg-amber-100 text-amber-800" },
  revisar: { label: "Revisar", cls: "bg-rose-100 text-rose-800" },
};

/** Configuración → Sistema: detecta fichas corregidas a mano con lotes aún en paquetes y aplica el desglose en bloque. */
export default function DesgloseAsistidoPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // producto_id → { sel, factor, precio_minimo, precio_sugerido }
  const [preview, setPreview] = useState(null);
  const [working, setWorking] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [actualizarCosto, setActualizarCosto] = useState(true);

  const analizar = async () => {
    setLoading(true); setPreview(null); setResultado(null);
    try {
      const { data } = await api.get("/productos/_/desglose-asistido");
      setData(data);
      const e = {};
      data.filas.forEach((f) => { e[f.producto_id] = { sel: f.confianza === "alta", factor: f.factor_sugerido || "", precio_minimo: f.precio_minimo || "", precio_sugerido: f.precio_sugerido || "", ventas_previas: "sub_paquete" }; });
      setEdits(e);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo analizar"); }
    finally { setLoading(false); }
  };

  const setEdit = (id, patch) => { setEdits((e) => ({ ...e, [id]: { ...e[id], ...patch } })); setPreview(null); };
  const setTodosVentas = (v) => { setEdits((e) => Object.fromEntries(Object.entries(e).map(([k, x]) => [k, { ...x, ventas_previas: v }]))); setPreview(null); };
  const toggleAll = (v) => { setEdits((e) => Object.fromEntries(Object.entries(e).map(([k, x]) => [k, { ...x, sel: v }]))); setPreview(null); };

  const items = () => (data?.filas || []).filter((f) => edits[f.producto_id]?.sel).map((f) => {
    const e = edits[f.producto_id];
    return { producto_id: f.producto_id, factor: Number(e.factor), precio_minimo: e.precio_minimo === "" ? null : Math.round(Number(e.precio_minimo)), precio_sugerido: e.precio_sugerido === "" ? null : Math.round(Number(e.precio_sugerido)), ventas_previas: e.ventas_previas || "sub_paquete" };
  });

  const validar = () => {
    const its = items();
    if (!its.length) { toast.error("Selecciona al menos un producto"); return null; }
    const malo = its.find((i) => !i.factor || i.factor <= 1);
    if (malo) { toast.error(`Escribe el factor (mayor que 1, ej. 20 o 12.5) en ${data.filas.find((f) => f.producto_id === malo.producto_id)?.producto}`); return null; }
    return its;
  };

  const calcular = async () => {
    const its = validar(); if (!its) return;
    setWorking(true);
    try { const { data: r } = await api.post("/productos/_/desglose-asistido/preview", { items: its, actualizar_costo_ficha: actualizarCosto }); setPreview(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular"); }
    finally { setWorking(false); }
  };

  const aplicar = async () => {
    const its = validar(); if (!its) return;
    if (!window.confirm(`Se aplicará el desglose a ${its.length} producto(s): los lotes pasan a unidades (cantidad ×N, costo ÷N) y se re-valoran los despachos históricos. El stock y los precios de la ficha se respetan. ¿Continuar?`)) return;
    setWorking(true);
    try {
      const { data: r } = await api.post("/productos/_/desglose-asistido/aplicar", { items: its, actualizar_costo_ficha: actualizarCosto });
      setResultado(r); setPreview(null);
      if (r.ok === 0) toast.error(`No se aplicó ningún desglose · ${r.con_error} con error`);
      else toast.success(`Desglose aplicado a ${r.ok} producto(s)${r.con_error ? ` · ${r.con_error} con error` : ""}`);
      await analizar();
      setResultado(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo aplicar"); }
    finally { setWorking(false); }
  };

  const seleccionados = Object.values(edits).filter((e) => e.sel).length;

  return (
    <Card className="p-5" data-testid="desglose-asistido-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground flex items-center gap-2"><Boxes className="w-4 h-4 text-primary" /> Desglose asistido de empaques</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Busca productos donde corregiste <b>a mano</b> el stock o el costo en la ficha (a unidades) pero los lotes siguen en paquetes. Sugiere el factor ×N, te deja revisar cada fila, omitir las que no apliquen y cambiar precios. Al aplicar, los lotes quedan en unidades y los despachos históricos se re-valoran.
          </p>
        </div>
        <Button onClick={analizar} disabled={loading} variant="outline" className="h-10" data-testid="desglose-asistido-analizar-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} {data ? "Volver a analizar" : "Analizar productos"}
        </Button>
      </div>

      {resultado && (
        <div className="mt-4 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/15 p-3 text-sm" data-testid="desglose-asistido-resultado">
          <div className="flex items-center gap-2 font-semibold text-emerald-800 dark:text-emerald-200"><CheckCircle2 className="w-4 h-4" /> Aplicado a {resultado.ok} producto(s){resultado.con_error ? ` · ${resultado.con_error} con error` : ""}</div>
          <ul className="mt-1 text-xs text-emerald-900/80 dark:text-emerald-100/80 space-y-0.5">
            {resultado.resultados.map((r) => <li key={r.producto_id}>{r.producto}: ×{r.factor} · stock {r.stock_viejo} → {r.stock_nuevo} · {r.lotes} lote(s) · {r.despachos_a_reexpresar} despacho(s) re-valorados</li>)}
            {resultado.errores.map((e) => <li key={e.producto_id} className="text-rose-700">✗ {e.error}</li>)}
          </ul>
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <Kpi label="Productos detectados" value={data.total} sub={`${data.por_confianza.alta} alta · ${data.por_confianza.media} media · ${data.por_confianza.revisar} revisar`} testid="desglose-asistido-total" />
            <Kpi label="Seleccionados" value={seleccionados} testid="desglose-asistido-seleccionados" />
            <Kpi label="Inventario inflado" value={formatCOP(data.inventario_inflado_total)} sub="Lotes a costo de paquete vs ficha" />
            <Kpi label="COGS histórico a recuperar" value={formatCOP(data.cogs_inflado_est_total)} sub="Con los factores sugeridos" accent />
          </div>
          {data.total === 0 && <div className="text-sm text-emerald-700" data-testid="desglose-asistido-vacio">No hay productos con ficha y lotes descuadrados. Todo en orden.</div>}
          <CostoRevisadoLista total={data.revisados} onCambio={analizar} />
          {data.total > 0 && (
            <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs" data-testid="desglose-asistido-toolbar">
                <span className="flex items-center gap-2">
                  <button type="button" className="text-primary hover:underline" onClick={() => toggleAll(true)} data-testid="desglose-asistido-sel-todos">Seleccionar todos</button>
                  <button type="button" className="text-primary hover:underline" onClick={() => toggleAll(false)} data-testid="desglose-asistido-sel-ninguno">Ninguno</button>
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">Ventas anteriores de todos:
                  <button type="button" className="text-primary hover:underline" onClick={() => setTodosVentas("sub_paquete")} data-testid="desglose-asistido-ventas-todos-unidad">por unidad</button> ·
                  <button type="button" className="text-primary hover:underline" onClick={() => setTodosVentas("paquete")} data-testid="desglose-asistido-ventas-todos-paquete">por paquete</button>
                </span>
                <label className="flex items-center gap-1.5 cursor-pointer" data-testid="desglose-asistido-actualizar-costo">
                  <input type="checkbox" checked={actualizarCosto} onChange={(e) => { setActualizarCosto(e.target.checked); setPreview(null); }} className="accent-primary" />
                  Actualizar el <b>costo de la ficha</b> al costo nuevo del lote
                </label>
              </div>
              <div className="text-[11px] text-muted-foreground">Por defecto solo vienen marcados los de confianza alta (stock y costo coinciden en el mismo ×N). Si el costo del lote es real (se compró caro y se vendió con pérdida), usa <b>Costo correcto</b> en la fila para que no vuelva a aparecer.</div>
              <div className="text-[11px] text-muted-foreground bg-muted/40 rounded-md p-2" data-testid="desglose-asistido-ventas-ayuda">
                <b>Ventas anteriores</b> decide cómo se corrigen los pedidos ya hechos: <b>Por unidad</b> = cada venta era un sub-paquete (la cantidad del pedido ya está bien; solo se corrige el costo ÷N y se recupera el stock que se descontó de más). <b>Por paquete</b> = se vendía el paquete completo (la cantidad vendida se multiplica ×N y el costo se divide; exige no tener pedidos abiertos). En ambos casos la rentabilidad se recalcula con el costo corregido.
              </div>
              <div className="max-h-[32rem] overflow-auto rounded-md border border-border">
                <table className="w-full text-[11px]">
                  <thead className="bg-muted/50 sticky top-0 z-10"><tr>
                    <th className="p-2"></th><th className="text-left p-2">Producto</th><th className="text-left p-2">Confianza</th>
                    <th className="text-right p-2">Stock ficha / lotes</th><th className="text-right p-2">Costo ficha / lote</th>
                    <th className="text-right p-2">Factor ×N</th><th className="text-left p-2">Ventas anteriores</th><th className="text-right p-2">Precio mín.</th><th className="text-right p-2">Precio sug.</th>
                    <th className="text-right p-2">COGS a recuperar</th><th className="text-left p-2">Señales</th><th className="p-2"></th>
                  </tr></thead>
                  <tbody>
                    {data.filas.map((f, i) => <Fila key={f.producto_id} f={f} i={i} e={edits[f.producto_id]} onEdit={(patch) => setEdit(f.producto_id, patch)} onRevisado={analizar} />)}
                  </tbody>
                </table>
              </div>
              {preview && <PreviewResumen preview={preview} />}
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button onClick={calcular} disabled={working || !seleccionados} variant="outline" className="h-10" data-testid="desglose-asistido-preview-btn">{working ? "Calculando..." : "Ver qué cambiaría"}</Button>
                <Button onClick={aplicar} disabled={working || !seleccionados} className="h-10" data-testid="desglose-asistido-aplicar-btn"><Wand2 className="w-4 h-4 mr-2" /> Aplicar desglose a {seleccionados} seleccionado(s)</Button>
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Fila({ f, i, e, onEdit, onRevisado }) {
  const c = CONF[f.confianza];
  return (
    <tr className={`border-t border-border/60 tabular-nums ${e?.sel ? "bg-primary/5" : ""}`} data-testid={`desglose-asistido-fila-${i}`}>
      <td className="p-2"><input type="checkbox" checked={!!e?.sel} onChange={(ev) => onEdit({ sel: ev.target.checked })} className="accent-primary" data-testid={`desglose-asistido-check-${i}`} /></td>
      <td className="p-2 max-w-[220px]"><div className="truncate font-medium">{f.producto}</div>{f.codigo && <div className="text-muted-foreground">{f.codigo}</div>}</td>
      <td className="p-2"><span className={`px-1.5 py-0.5 rounded font-semibold ${c.cls}`}>{c.label}</span></td>
      <td className="p-2 text-right">{f.stock_ficha.toLocaleString("es-CO")} / <span className="text-muted-foreground">{f.stock_lotes.toLocaleString("es-CO")}</span></td>
      <td className="p-2 text-right">{formatCOP(f.costo_ficha)} / <span className="text-rose-600">{formatCOP(f.costo_lote)}</span></td>
      <td className="p-2"><Input type="number" min="1.01" step="0.01" value={e?.factor ?? ""} onChange={(ev) => onEdit({ factor: ev.target.value })} className="h-8 w-20 text-right ml-auto" placeholder="×? (ej. 12.5)" data-testid={`desglose-asistido-factor-${i}`} /></td>
      <td className="p-2">
        <select value={e?.ventas_previas || "sub_paquete"} onChange={(ev) => onEdit({ ventas_previas: ev.target.value })} className="h-8 rounded-md border border-input bg-background px-2 text-[11px]" data-testid={`desglose-asistido-ventas-${i}`}>
          <option value="sub_paquete">Por unidad</option>
          <option value="paquete">Por paquete</option>
        </select>
      </td>
      <td className="p-2"><Input type="number" min="0" value={e?.precio_minimo ?? ""} onChange={(ev) => onEdit({ precio_minimo: ev.target.value })} className="h-8 w-24 text-right ml-auto" data-testid={`desglose-asistido-pmin-${i}`} /></td>
      <td className="p-2"><Input type="number" min="0" value={e?.precio_sugerido ?? ""} onChange={(ev) => onEdit({ precio_sugerido: ev.target.value })} className="h-8 w-24 text-right ml-auto" data-testid={`desglose-asistido-psug-${i}`} /></td>
      <td className="p-2 text-right">{f.cogs_inflado_est ? formatCOP(f.cogs_inflado_est) : "—"}<div className="text-muted-foreground">{f.despachos} desp.</div></td>
      <td className="p-2 text-muted-foreground">{f.senales.join(" · ")}</td>
      <td className="p-2"><BotonCostoCorrecto productoId={f.producto_id} nombre={f.producto} onDone={onRevisado} testid={`desglose-asistido-costo-ok-${i}`} /></td>
    </tr>
  );
}

function PreviewResumen({ preview }) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3 text-xs space-y-1" data-testid="desglose-asistido-preview">
      <div className="font-semibold">Vista previa · {preview.ok} producto(s) listos{preview.con_error ? ` · ${preview.con_error} con error` : ""}</div>
      {preview.resultados.map((r) => (
        <div key={r.producto_id} className="flex flex-wrap gap-x-3 tabular-nums">
          <span className="font-medium">{r.producto}</span>
          <span>×{r.factor} · {r.ventas_previas === "paquete" ? "ventas por paquete" : "ventas por unidad"}</span>
          <span>stock {r.stock_viejo} → <b>{r.stock_nuevo}</b></span>
          <span>costo ficha {formatCOP(r.costo_ficha_viejo)} → <b>{formatCOP(r.costo_ficha_nuevo)}</b></span>
          <span>costo lote → <b>{formatCOP(r.filas?.[0]?.costo_nuevo)}</b></span>
          <span>precios {formatCOP(r.precio_minimo_nuevo)} / {formatCOP(r.precio_sugerido_nuevo)}</span>
          <span className="text-muted-foreground">{r.lotes} lote(s) · {r.despachos_a_reexpresar} despacho(s)</span>
          {r.costo_ficha_nuevo > 0 && r.filas?.[0]?.costo_nuevo > 0 && Math.abs(r.filas[0].costo_nuevo - r.costo_ficha_nuevo) / r.costo_ficha_nuevo > 0.1 && (
            <span className="text-rose-700 flex items-center gap-1" data-testid="desglose-asistido-warn-factor"><AlertTriangle className="w-3 h-3" />El costo del lote (÷{r.factor}) no coincide con el de la ficha: revisa el factor.</span>
          )}
          {(r.advertencias || []).map((a, i) => <span key={i} className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{a}</span>)}
        </div>
      ))}
      {preview.errores.map((e) => <div key={e.producto_id} className="text-rose-700">✗ {e.error}</div>)}
    </div>
  );
}

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-lg border p-2.5 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-muted/20"}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-base font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
